import { useState } from 'react';
import { Badge, Button, Card, Field, Input, useToast } from '@/components/ui';
import { teleApi } from '@/features/teleconsults/api';
import { useVideoAction, videoApi } from '@/features/video-consults/api';
import {
  DEFAULT_CHARGEABLE,
  END_REASON_LABEL,
  OUTCOME_HINT,
  OUTCOME_LABEL,
} from '@/features/video-consults/format';
import {
  OUTCOME_RESULTS,
  type StaffVideoSession,
  type VideoOutcomeResult,
} from '@/features/video-consults/types';
import { klDate, klTime } from '@/pages/calendar/time';
import parity from '@/pages/ops/parity.module.css';
import { VideoError } from './VideoError';
import styles from './video.module.css';

const SOURCE_LABEL = {
  doctor: 'by the doctor',
  patient: 'by the patient (“I can’t rejoin”)',
  system: 'automatically: the 10-minute rejoin window ran out',
} as const;

/** After the call (RL-1004): how it ended, the outcome, then close the teleconsult. */
export function AfterCallPanel({
  s,
  canManage,
}: {
  s: StaffVideoSession;
  canManage: boolean;
}) {
  return (
    <div className={styles.layout}>
      <Card title="After the call">
        <div className={styles.stack}>
          <dl className={parity.kv}>
            <dt>Ended</dt>
            <dd>
              {s.endReason ? END_REASON_LABEL[s.endReason] : 'Over'}
              {s.endedAt && ` · ${klTime(s.endedAt)}`}
            </dd>
            <dt>Outcome</dt>
            <dd>
              {s.outcome ? (
                <>
                  {OUTCOME_LABEL[s.outcome.result]}
                  <span className={parity.sub}>
                    Recorded {SOURCE_LABEL[s.outcome.source]} at{' '}
                    {klTime(s.outcome.recordedAt)} ·{' '}
                    {s.outcome.chargeable ? 'fee stands' : 'nothing charged'}
                    {s.outcome.followUpBy &&
                      ` · come in / book by ${s.outcome.followUpBy}`}
                  </span>
                  {s.outcome.patientNote && (
                    <span className={parity.sub}>
                      Note for the patient: “{s.outcome.patientNote}”
                    </span>
                  )}
                </>
              ) : s.outcomePending ? (
                <Badge tone="warning">Outcome needed</Badge>
              ) : (
                'None'
              )}
            </dd>
          </dl>
          {s.endReason === 'patient_dropped' && (
            <p className={styles.warn} role="status">
              The patient left and did not rejoin
              {s.rejoinBy ? ` by ${klTime(s.rejoinBy)}` : ' within 10 minutes'},
              so the consult ended as dropped. Correct the outcome below if
              needed.
            </p>
          )}
          {s.payment.refundDue && (
            <p className={styles.warn} role="status">
              The patient already paid this fee but it no longer stands: refund
              it in Billing
              {s.payment.reference ? ` (bill ${s.payment.reference})` : ''}.
            </p>
          )}
          {!s.admittedAt && (
            <p className={parity.infoNotice}>
              Nobody was let into this call, so there is no outcome to record.
            </p>
          )}
        </div>
      </Card>
      {canManage && s.admittedAt && <OutcomeForm s={s} />}
      {canManage && s.teleconsultStatus === 'in_progress' && (
        <CloseTeleconsult s={s} />
      )}
    </div>
  );
}

function OutcomeForm({ s }: { s: StaffVideoSession }) {
  const toast = useToast();
  const save = useVideoAction(videoApi.outcome);
  const [result, setResult] = useState<VideoOutcomeResult | ''>(
    s.outcome?.result ?? '',
  );
  const [chargeable, setChargeable] = useState<boolean | null>(
    s.outcome?.chargeable ?? null,
  );
  const [note, setNote] = useState(s.outcome?.patientNote ?? '');
  const [followUpBy, setFollowUpBy] = useState(s.outcome?.followUpBy ?? '');
  const [error, setError] = useState<unknown>(null);
  const effectiveChargeable =
    chargeable ?? (result ? DEFAULT_CHARGEABLE[result] : false);
  const submit = () => {
    if (!result) return;
    setError(null);
    save
      .mutateAsync({
        id: s.id,
        version: s.version,
        outcome: {
          result,
          patientNote: note.trim() || null,
          chargeable: effectiveChargeable,
          followUpBy: followUpBy || null,
        },
      })
      .then(() => toast.show({ tone: 'success', title: 'Outcome recorded' }))
      .catch((e: unknown) => setError(e));
  };
  return (
    <Card title={s.outcome ? 'Correct the outcome' : 'Record the outcome'}>
      <div className={styles.stack}>
        {error !== null && <VideoError error={error} />}
        <fieldset className={styles.radios}>
          <legend className="eyebrow">How did the consult end?</legend>
          {OUTCOME_RESULTS.map((r) => (
            <label key={r} className={styles.radio}>
              <input
                type="radio"
                name="outcome"
                value={r}
                checked={result === r}
                onChange={() => {
                  setResult(r);
                  setChargeable(null);
                }}
              />
              <span>
                {OUTCOME_LABEL[r]}
                <span className={parity.sub}>{OUTCOME_HINT[r]}</span>
              </span>
            </label>
          ))}
        </fieldset>
        <label className={styles.checkbox}>
          <input
            type="checkbox"
            checked={effectiveChargeable}
            disabled={!result}
            onChange={(e) => setChargeable(e.target.checked)}
          />
          The consult fee stands
        </label>
        <Field
          label="Note for the patient (optional)"
          hint="Shown in the app as written; not the clinical note. Up to 500 characters."
        >
          <textarea
            className={styles.textarea}
            maxLength={500}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </Field>
        <Field
          label="Come in / book again by (optional)"
          hint={
            result === 'come_in'
              ? 'Empty = today.'
              : 'Not before the consult day.'
          }
        >
          <Input
            type="date"
            min={klDate(s.startsAt)}
            value={followUpBy}
            onChange={(e) => setFollowUpBy(e.target.value)}
          />
        </Field>
        <div>
          <Button
            variant="primary"
            disabled={!result}
            loading={save.isPending}
            onClick={submit}
          >
            {s.outcome ? 'Save correction' : 'Record outcome'}
          </Button>
        </div>
      </div>
    </Card>
  );
}

/** Close the teleconsult with the clinical notes (POST /teleconsults/{id}/close). */
function CloseTeleconsult({ s }: { s: StaffVideoSession }) {
  const toast = useToast();
  const close = useVideoAction(teleApi.close);
  const [notes, setNotes] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<unknown>(null);
  const first = s.type === 'first_visit';
  const ok = notes.trim().length >= 5 && (!first || reason.trim().length >= 10);
  return (
    <Card title="Close the teleconsult">
      <div className={styles.stack}>
        {error !== null && <VideoError error={error} />}
        {first && (
          <Field label="Reason this first visit is by video (MMC cl. 15)">
            <textarea
              className={styles.textarea}
              maxLength={250}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </Field>
        )}
        <Field label="Consult notes">
          <textarea
            className={styles.textarea}
            rows={5}
            maxLength={700}
            value={notes}
            placeholder="Presenting complaint, assessment, plan…"
            onChange={(e) => setNotes(e.target.value)}
          />
        </Field>
        <div>
          <Button
            variant="primary"
            disabled={!ok}
            loading={close.isPending}
            onClick={() => {
              setError(null);
              close
                .mutateAsync({
                  id: s.teleconsultId,
                  notes: notes.trim(),
                  ...(first ? { firstVisitReason: reason.trim() } : {}),
                })
                .then(() =>
                  toast.show({ tone: 'success', title: 'Teleconsult closed' }),
                )
                .catch((e: unknown) => setError(e));
            }}
          >
            Close teleconsult
          </Button>
        </div>
      </div>
    </Card>
  );
}
